import { useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { getConsentedUsers, type SmsFilter } from "@/requests/internal-sms/sms-notifications";
import { SMS_MAX_MESSAGE_LENGTH, applyFilter } from "@shared/sms-filters";

export default function SmsNotificationsPage() {
  const [, setLocation] = useLocation();
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState<SmsFilter>("all");
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ["sms-consented-users"],
    queryFn: getConsentedUsers,
  });

  const filteredUsers = useMemo(
    () => (previewData ? applyFilter(previewData.users, filter) : []),
    [previewData, filter],
  );

  const handlePreview = () => {
    if (!message.trim()) {
      setValidationError("Please enter a message");
      return;
    }
    if (message.length > SMS_MAX_MESSAGE_LENGTH) {
      setValidationError(`Message must be ${SMS_MAX_MESSAGE_LENGTH} characters or less`);
      return;
    }
    setValidationError(null);
    setLocation(`/internal/sms-notifications/confirm?filter=${filter}&message=${encodeURIComponent(message)}`);
  };

  const remainingChars = SMS_MAX_MESSAGE_LENGTH - message.length;

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Internal</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">SMS Notifications</span>
      </div>
    }>
      <div className="container mx-auto py-6 max-w-2xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <MessageSquare className="h-8 w-8" />
            SMS Notifications
          </h1>
          <p className="text-muted-foreground mt-2">
            Send SMS notifications to users based on filter criteria
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Compose Message</CardTitle>
            <CardDescription>
              Create an SMS notification to send to selected users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Enter your message here..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setValidationError(null);
                }}
                maxLength={SMS_MAX_MESSAGE_LENGTH}
                rows={6}
                className="resize-none"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{remainingChars} characters remaining</span>
                <span>{message.length} / {SMS_MAX_MESSAGE_LENGTH}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter">Recipients</Label>
              <Select value={filter} onValueChange={(value: SmsFilter) => setFilter(value)}>
                <SelectTrigger id="filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users (consented only)</SelectItem>
                  <SelectItem value="post-pilot">Post-pilot users</SelectItem>
                  <SelectItem value="internal">Internal (@onsemble.com)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {filter === "all" && "All users who have consented to SMS notifications"}
                {filter === "post-pilot" && "Users created after February 5, 2026 who have consented"}
                {filter === "internal" && "Users with @onsemble.com email addresses who have consented"}
              </p>
              {!isLoadingPreview && previewData && (
                <p className="text-sm font-medium">
                  {filteredUsers.length} recipient{filteredUsers.length !== 1 ? "s" : ""} match this filter
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handlePreview}
                disabled={!message.trim() || message.length > SMS_MAX_MESSAGE_LENGTH}
                className="flex-1"
              >
                Preview Recipients & Send
              </Button>
            </div>

            {validationError && (
              <div className="text-sm text-destructive">
                {validationError}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
